import React, { useEffect, useMemo, useState } from 'react';
import { BriefcaseBusiness, RefreshCw, Users } from 'lucide-react';
import api from '../../api';
import Alert from '../../components/Alert/Alert';

const getListFromResponse = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data?.content)) return payload.data.content;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const getProjectCode = (project) => project?.projectCode || project?.code || project?.project_id || '-';
const getProjectClient = (project) => project?.clientName || project?.companyName || project?.client?.name || project?.company?.companyName || '-';
const getProjectStandard = (project) => {
  if (Array.isArray(project?.standards)) {
    const standards = project.standards
      .map((standard) => standard?.name || standard?.standardName || standard)
      .filter(Boolean);
    return standards.length > 0 ? standards.join(', ') : '-';
  }
  return project?.standardName || project?.standard || project?.scheme || '-';
};

const getProjectStatus = (project) => project?.status || project?.schedulerStatus || 'Pending Assignment';

const getSchedulerName = (scheduler) => scheduler?.name || scheduler?.fullName || scheduler?.userName || scheduler?.schedulerName || '-';
const getSchedulerAssignments = (scheduler) => Number(
  scheduler?.currentAssignments ?? scheduler?.assignmentCount ?? scheduler?.loadCount ?? scheduler?.currentLoad ?? 0,
);
const getSchedulerCapacity = (scheduler) => Number(
  scheduler?.capacity ?? scheduler?.maxCapacity ?? scheduler?.availableCapacity ?? scheduler?.totalCapacity ?? 0,
);

const normalizeSchedulerStatus = (scheduler) => {
  const rawStatus = String(scheduler?.status || '').trim().toLowerCase();
  if (rawStatus.includes('near')) return 'Near Capacity';
  if (rawStatus.includes('full')) return 'Full';
  if (rawStatus.includes('avail')) return 'Available';

  const assignments = getSchedulerAssignments(scheduler);
  const capacity = getSchedulerCapacity(scheduler);

  if (capacity > 0 && assignments >= capacity) return 'Full';
  if (capacity > 0 && assignments / capacity >= 0.8) return 'Near Capacity';
  return 'Available';
};

const getStatusBadgeClass = (status) => {
  if (status === 'Full') return 'badge bg-danger';
  if (status === 'Near Capacity') return 'badge bg-warning text-dark';
  return 'badge bg-success';
};

const getProjectId = (project) => project?.id || project?.projectId;

const LoadBalancer = () => {
  const [projects, setProjects] = useState([]);
  const [schedulers, setSchedulers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedSchedulerId, setSelectedSchedulerId] = useState('');
  const [alert, setAlert] = useState(null);

  const loadData = async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      setAlert(null);
      const [projectsResponse, schedulerResponse] = await Promise.all([
        api.get('/audit/projects/unassigned-scheduler'),
        api.get('/audit/schedulers/workload'),
      ]);

      setProjects(getListFromResponse(projectsResponse?.data));
      setSchedulers(getListFromResponse(schedulerResponse?.data));
    } catch (error) {
      console.error('Error loading load balancer data:', error);
      setAlert({
        type: 'danger',
        message: error?.response?.data?.message || 'Failed to load load balancer data.',
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const schedulerOptions = useMemo(() => schedulers.map((scheduler) => {
    const assignments = getSchedulerAssignments(scheduler);
    const capacity = getSchedulerCapacity(scheduler);
    return {
      id: scheduler?.id || scheduler?.userId,
      label: `${getSchedulerName(scheduler)} (${assignments}/${capacity || 0})`,
    };
  }).filter((scheduler) => scheduler.id != null), [schedulers]);

  const openAssignModal = (project) => {
    setSelectedProject(project);
    setSelectedSchedulerId('');
    setShowAssignModal(true);
  };

  const closeAssignModal = (force = false) => {
    if (submitting && !force) return;
    setShowAssignModal(false);
    setSelectedProject(null);
    setSelectedSchedulerId('');
  };

  const handleAssignSubmit = async (event) => {
    event.preventDefault();
    const projectId = getProjectId(selectedProject);

    if (!projectId || !selectedSchedulerId) {
      setAlert({ type: 'danger', message: 'Please select a scheduler before submitting.' });
      return;
    }

    setSubmitting(true);
    try {
      const normalizedSchedulerId = /^\d+$/.test(String(selectedSchedulerId))
        ? Number(selectedSchedulerId)
        : selectedSchedulerId;

      await api.post(`/audit/projects/${projectId}/assign-scheduler`, {
        schedulerId: normalizedSchedulerId,
      });

      closeAssignModal(true);
      setAlert({ type: 'success', message: 'Scheduler assigned successfully.' });
      await loadData({ silent: true });
    } catch (error) {
      console.error('Error assigning scheduler:', error);
      setAlert({
        type: 'danger',
        message: error?.response?.data?.message || 'Failed to assign scheduler.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-xl">
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      <div className="row g-2 align-items-center mb-3">
        <div className="col">
          <h3 className="page-title m-0">Tools Load Balancer</h3>
          <div className="text-muted mt-1">Monitor unassigned projects and scheduler capacity from a single workspace.</div>
        </div>
        <div className="col-auto ms-auto d-print-none">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => loadData({ silent: true })}
            disabled={refreshing || loading}
          >
            <RefreshCw className="icon me-2" size={18} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-lg-6">
          <div className="card h-100">
            <div className="card-header">
              <h3 className="card-title mb-0 d-flex align-items-center gap-2">
                <BriefcaseBusiness className="icon text-primary" size={18} />
                Projects Waiting for Scheduler Assignment
              </h3>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="d-flex align-items-center justify-content-center py-5">
                  <div className="spinner-border text-primary" role="status" />
                </div>
              ) : projects.length === 0 ? (
                <div className="p-4 text-muted text-center">No unassigned projects found.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-vcenter card-table table-striped mb-0">
                    <thead>
                      <tr>
                        <th>Project Code</th>
                        <th>Client</th>
                        <th>Standard</th>
                        <th>Submission Date</th>
                        <th>Status</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projects.map((project) => {
                        const projectId = getProjectId(project);
                        return (
                          <tr key={projectId || `${getProjectCode(project)}-${getProjectClient(project)}`}>
                            <td className="fw-semibold">{getProjectCode(project)}</td>
                            <td>{getProjectClient(project)}</td>
                            <td>{getProjectStandard(project)}</td>
                            <td>{formatDate(project?.submissionDate || project?.submittedAt || project?.createdAt)}</td>
                            <td>
                              <span className="badge bg-warning text-dark">{getProjectStatus(project)}</span>
                            </td>
                            <td className="text-end">
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => openAssignModal(project)}
                                disabled={schedulerOptions.length === 0}
                              >
                                Assign Scheduler
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="card h-100">
            <div className="card-header">
              <h3 className="card-title mb-0 d-flex align-items-center gap-2">
                <Users className="icon text-primary" size={18} />
                Scheduler Workload Overview
              </h3>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="d-flex align-items-center justify-content-center py-5">
                  <div className="spinner-border text-primary" role="status" />
                </div>
              ) : schedulers.length === 0 ? (
                <div className="p-4 text-muted text-center">No scheduler workload data available.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-vcenter card-table table-striped mb-0">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Current Assignments</th>
                        <th>Capacity</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schedulers.map((scheduler) => {
                        const status = normalizeSchedulerStatus(scheduler);
                        return (
                          <tr key={scheduler?.id || scheduler?.userId || getSchedulerName(scheduler)}>
                            <td className="fw-semibold">{getSchedulerName(scheduler)}</td>
                            <td>{getSchedulerAssignments(scheduler)}</td>
                            <td>{getSchedulerCapacity(scheduler)}</td>
                            <td>
                              <span className={getStatusBadgeClass(status)}>{status}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showAssignModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055 }}
          onClick={closeAssignModal}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(event) => event.stopPropagation()}>
            <div className="modal-content">
              <form onSubmit={handleAssignSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">Assign Scheduler</h5>
                  <button type="button" className="btn-close" onClick={closeAssignModal} disabled={submitting} />
                </div>
                <div className="modal-body">
                  <div className="mb-3 p-3 bg-light rounded">
                    <div className="fw-semibold">{getProjectCode(selectedProject)}</div>
                    <div className="text-muted small">{getProjectClient(selectedProject)}</div>
                    <div className="text-muted small">{getProjectStandard(selectedProject)}</div>
                  </div>

                  <div className="mb-0">
                    <label htmlFor="schedulerId" className="form-label fw-semibold">Scheduler</label>
                    <select
                      id="schedulerId"
                      className="form-select"
                      value={selectedSchedulerId}
                      onChange={(event) => setSelectedSchedulerId(event.target.value)}
                      disabled={submitting || schedulerOptions.length === 0}
                    >
                      <option value="">Select scheduler</option>
                      {schedulerOptions.map((scheduler) => (
                        <option key={scheduler.id} value={scheduler.id}>{scheduler.label}</option>
                      ))}
                    </select>
                    {schedulerOptions.length === 0 && (
                      <div className="form-text text-danger">No scheduler is currently available for assignment.</div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-link link-secondary" onClick={closeAssignModal} disabled={submitting}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={submitting || !selectedSchedulerId}>
                    {submitting ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoadBalancer;
